import { IDocumentRelation } from '@query/documentRelations'
import { IDocument } from '@query/documents'

import { buildRelatedDocumentSummary } from './relationAmounts'

export const hasRelationSelectionChanges = (current: number[], initial: number[]): boolean => {
  if (current.length !== initial.length) {
    return true
  }
  const initialSet = new Set(initial)
  return current.some(id => !initialSet.has(id))
}

export const mapRelationsForForm = (relations?: IDocumentRelation[] | null) =>
  relations?.map(relation => ({
    ...relation,
    _optimisticId: relation.id ? `existing-${relation.id}` : `default-${relation.related_document}`
  })) ?? []

export const buildPickerDocumentFromRelation = (
  relation: Pick<IDocumentRelation, 'related_document' | 'related_document_summary'>
): IDocument | null => {
  const summary = relation.related_document_summary
  if (!summary) {
    return null
  }

  const amount = summary.amount
  return {
    id: relation.related_document,
    number: summary.number,
    date: summary.date ?? '',
    contact_name: summary.contact_name ?? '',
    balance: amount,
    relation_entry_signals: {
      due_account_effect: amount,
      due_account_types: [],
      has_ass_pay_entries: false
    },
    description: '',
    is_draft: false
  } as IDocument
}

export const getPreferredBalanceSign = (signedAmount?: number | null): 1 | -1 | null => {
  if (signedAmount == null || signedAmount === 0) {
    return null
  }
  return signedAmount > 0 ? 1 : -1
}

/** Prefer documents whose balance matches the expected payment/invoice direction. */
export const compareDocumentsByPreferredSign = (
  left: Pick<IDocument, 'balance' | 'relation_network_signals'>,
  right: Pick<IDocument, 'balance' | 'relation_network_signals'>,
  preferredSign: 1 | -1 | null
): number => {
  if (preferredSign == null) {
    return 0
  }

  const score = (document: Pick<IDocument, 'balance' | 'relation_network_signals'>): number => {
    const nestedDue =
      document.relation_network_signals?.SALARY_PAYMENT?.due_effect ??
      document.relation_network_signals?.ACCRUAL_PAIR?.due_effect
    const signed = nestedDue != null && nestedDue !== 0 ? nestedDue : document.balance ?? 0
    if (signed === 0) {
      return 0
    }
    return Math.sign(signed) === preferredSign ? 1 : -1
  }

  return score(right) - score(left)
}

export const buildOptimisticDocumentRelation = (
  relation: Pick<IDocumentRelation, 'related_document' | 'role' | 'type'>,
  options: {
    cachedDocument?: IDocument | null
    optimisticIdSuffix?: string | number
  } = {}
): Pick<IDocumentRelation, 'related_document' | 'role' | 'type' | 'related_document_summary'> & {
  _optimisticId: string
} => {
  const suffix = options.optimisticIdSuffix
  const idPart = suffix == null || suffix === '' ? `${Date.now()}` : `${Date.now()}-${suffix}`

  return {
    related_document: relation.related_document,
    role: relation.role,
    type: relation.type,
    _optimisticId: `temp-${idPart}`,
    ...(options.cachedDocument
      ? { related_document_summary: buildRelatedDocumentSummary(options.cachedDocument) }
      : {})
  }
}
